import type { LibraryEntryResponse } from '@malaberg/shared';
import { Badge, Combobox, Group, Input, Text, useCombobox } from '@mantine/core';
import { notifications } from '@mantine/notifications';
/**
 * IngredientsMultiSelect — T23 + T6.
 *
 * Multi-select variant for the "Key ingredients" field. Each selection is an
 * entryId. Add-new flow identical to LibraryCombobox.
 *
 * Uses a custom Combobox (not Mantine MultiSelect) to support the Add-new flow.
 *
 * T6 — Ephemeral support:
 * - Optional `fieldValues?: FieldValue[]` prop for the 3-channel model.
 * - When a FieldValue has kind='ephemeral', its chip shows a subtle italic style
 *   and data-testid="ephemeral-chip-<index>".
 * - Removing a chip works regardless of kind.
 * - The `value` / `onChange` props remain for backward compat with BriefForm.tsx
 *   (BriefForm reads ingredientEntryIds and calls setField('ingredientEntryIds', ids)).
 *
 * When `fieldValues` is provided it takes precedence for rendering chips.
 * When undefined, the old `value` (string[]) is used to derive display.
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import { librariesApi, unwrapLibraryEntryResponse } from '../../api/endpoints/libraries.js';
import { ApiError } from '../../api/errors.js';
import { useLibraries } from '../../hooks/use-libraries.js';
import { useBriefFormStore } from '../../state/brief-form-store.js';
import type { FieldValue } from '../../state/brief-form-store.js';
import { AddNewConfirmRow } from './AddNewConfirmRow.js';
import { sanitize } from './LibraryCombobox.js';

const ADD_NEW_VALUE = '__add_new__';

interface IngredientsMultiSelectProps {
  /** Backward-compat: entryIds only. BriefForm passes this until T8 migration. */
  value: string[];
  onChange: (entryIds: string[]) => void;
  /**
   * T6: 3-channel field values. When provided, used for rendering chips.
   * Ephemeral chips get a subtle italic indicator.
   */
  fieldValues?: FieldValue[];
  /** T6: Called when the full field-value array changes (add, remove, ephemeral chip remove). */
  onFieldValuesChange?: (fvs: FieldValue[]) => void;
}

// ─── Chip rendering ───────────────────────────────────────────────────────────

interface ChipItem {
  /** Display text */
  label: string;
  /** Key for React */
  key: string;
  /** True when this chip is from an ephemeral AI suggestion */
  isEphemeral: boolean;
  testId: string;
}

export function IngredientsMultiSelect({
  value,
  onChange,
  fieldValues,
  onFieldValuesChange,
}: IngredientsMultiSelectProps) {
  const store = useCombobox({
    onDropdownClose: () => store.resetSelectedOption(),
  });

  const productId = useBriefFormStore((s) => s.productId);
  const scope = productId ? `product:${productId}` : null;

  const { entries, refetch } = useLibraries('ingredient', scope);

  const [inputValue, setInputValue] = useState('');
  const [isAdding, setIsAdding] = useState(false);
  const blurTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    return () => {
      if (blurTimerRef.current !== null) {
        clearTimeout(blurTimerRef.current);
      }
    };
  }, []);

  // ── Derive chip items for rendering ───────────────────────────────────────
  const chips: ChipItem[] = fieldValues
    ? fieldValues.map((fv, i) => {
        if (fv.kind === 'ephemeral') {
          return {
            label: fv.ephemeralValue,
            key: `ephemeral-${i}`,
            isEphemeral: true,
            testId: `ephemeral-chip-${i}`,
          };
        }
        if (fv.kind === 'entry') {
          const e = entries.find((en) => en.id === fv.entryId);
          return {
            label: e?.displayName ?? fv.entryId,
            key: fv.entryId,
            isEphemeral: false,
            testId: `ingredient-chip-${fv.entryId}`,
          };
        }
        // kind === 'value' (writer-typed, pending library creation)
        return {
          label: fv.value,
          key: `value-${i}`,
          isEphemeral: false,
          testId: `value-chip-${i}`,
        };
      })
    : // Backward compat: derive from entryIds
      value
        .map((id) => entries.find((e) => e.id === id))
        .filter((e): e is LibraryEntryResponse => e !== undefined)
        .map((e) => ({
          label: e.displayName,
          key: e.id,
          isEphemeral: false,
          testId: `ingredient-chip-${e.id}`,
        }));

  // ── Remove a chip ─────────────────────────────────────────────────────────
  const removeChip = useCallback(
    (chipKey: string, chipIndex: number) => {
      if (fieldValues && onFieldValuesChange) {
        // New path: operate on fieldValues array
        const next = fieldValues.filter((_, i) => i !== chipIndex);
        onFieldValuesChange(next);
        // Also keep backward-compat onChange in sync (entry chips only)
        onChange(
          next
            .filter((fv): fv is { kind: 'entry'; entryId: string } => fv.kind === 'entry')
            .map((fv) => fv.entryId)
        );
      } else {
        // Old path: operate on entryIds
        onChange(value.filter((id) => id !== chipKey));
      }
    },
    [fieldValues, onFieldValuesChange, onChange, value]
  );

  // ── Filter entries for dropdown ────────────────────────────────────────────
  const selectedEntryIds = fieldValues
    ? fieldValues
        .filter((fv): fv is { kind: 'entry'; entryId: string } => fv.kind === 'entry')
        .map((fv) => fv.entryId)
    : value;

  const query = inputValue.toLowerCase();
  const filtered = entries.filter((e) => {
    const matchesQuery = query ? e.displayName.toLowerCase().includes(query) : true;
    const notSelected = !selectedEntryIds.includes(e.id);
    return matchesQuery && notSelected;
  });

  const hasExactMatch = entries.some(
    (e) => e.displayName.toLowerCase() === inputValue.toLowerCase()
  );

  const showAddNew = inputValue.trim().length > 0 && !hasExactMatch;

  const handleAddNew = useCallback(async () => {
    if (isAdding || !scope) return;

    const typed = inputValue.trim();
    if (!typed) return;

    setIsAdding(true);

    try {
      const res = await librariesApi.create('ingredient', {
        scope,
        value: sanitize(typed),
        displayName: typed,
        metadata: {},
      });

      const created = unwrapLibraryEntryResponse(res);
      await refetch();
      setInputValue('');
      const newEntryIds = [...value, created.id];
      onChange(newEntryIds);
      if (fieldValues !== undefined && onFieldValuesChange) {
        onFieldValuesChange([...(fieldValues ?? []), { kind: 'entry', entryId: created.id }]);
      }
      store.closeDropdown();
    } catch (err) {
      if (err instanceof ApiError && err.status === 409 && err.code === 'duplicate_value') {
        const sanitized = sanitize(typed);
        const existing = entries.find(
          (e) => e.value === sanitized || e.displayName.toLowerCase() === typed.toLowerCase()
        );
        if (existing && !selectedEntryIds.includes(existing.id)) {
          setInputValue('');
          onChange([...value, existing.id]);
          if (fieldValues !== undefined && onFieldValuesChange) {
            onFieldValuesChange([...(fieldValues ?? []), { kind: 'entry', entryId: existing.id }]);
          }
          store.closeDropdown();
        }
      } else {
        notifications.show({
          title: 'Error',
          message: err instanceof Error ? err.message : 'Failed to add ingredient',
          color: 'red',
        });
      }
    } finally {
      setIsAdding(false);
    }
  }, [
    entries,
    fieldValues,
    inputValue,
    isAdding,
    onChange,
    onFieldValuesChange,
    refetch,
    scope,
    store,
    value,
  ]);

  const handleOptionSubmit = useCallback(
    async (optionValue: string) => {
      if (optionValue === ADD_NEW_VALUE) {
        await handleAddNew();
        return;
      }

      const entry = entries.find((e) => e.id === optionValue);
      if (entry && !selectedEntryIds.includes(entry.id)) {
        setInputValue('');
        onChange([...value, entry.id]);
        if (fieldValues !== undefined && onFieldValuesChange) {
          onFieldValuesChange([...(fieldValues ?? []), { kind: 'entry', entryId: entry.id }]);
        }
        store.closeDropdown();
      }
    },
    [
      entries,
      fieldValues,
      handleAddNew,
      onChange,
      onFieldValuesChange,
      selectedEntryIds,
      store,
      value,
    ]
  );

  return (
    <Combobox
      store={store}
      onOptionSubmit={(optVal) => {
        void handleOptionSubmit(optVal);
      }}
    >
      <Combobox.Target>
        <Input.Wrapper label="Key ingredients">
          {/* Selected pills */}
          {chips.length > 0 && (
            <Group gap="xs" mb="xs">
              {chips.map((chip, i) => (
                <Badge
                  key={chip.key}
                  variant="light"
                  fs={chip.isEphemeral ? 'italic' : undefined}
                  data-testid={chip.testId}
                  rightSection={
                    <Text
                      component="span"
                      size="xs"
                      style={{ cursor: 'pointer' }}
                      onClick={() => removeChip(chip.key, i)}
                      data-testid={`remove-ingredient-${chip.key}`}
                    >
                      ×
                    </Text>
                  }
                >
                  {chip.label}
                </Badge>
              ))}
            </Group>
          )}
          <Input
            component="input"
            placeholder="Search or add ingredients"
            data-testid="field-key-ingredients"
            value={inputValue}
            onChange={(event) => {
              setInputValue(event.currentTarget.value);
              store.openDropdown();
              store.updateSelectedOptionIndex();
            }}
            onFocus={() => store.openDropdown()}
            onBlur={() => {
              blurTimerRef.current = setTimeout(() => store.closeDropdown(), 150);
            }}
            onClick={() => store.openDropdown()}
          />
        </Input.Wrapper>
      </Combobox.Target>

      <Combobox.Dropdown>
        <Combobox.Options>
          {filtered.length === 0 && !showAddNew && (
            <Combobox.Empty>
              <Text size="sm" c="dimmed">
                No entries
              </Text>
            </Combobox.Empty>
          )}

          {filtered.map((entry) => (
            <Combobox.Option key={entry.id} value={entry.id}>
              {entry.displayName}
            </Combobox.Option>
          ))}

          {showAddNew && (
            <AddNewConfirmRow
              typedValue={inputValue.trim()}
              typeName="ingredient"
              optionValue={ADD_NEW_VALUE}
              disabled={isAdding}
            />
          )}
        </Combobox.Options>
      </Combobox.Dropdown>
    </Combobox>
  );
}
